import { useEffect, useState } from "react";
import Drawer from "../../../../components/drawers/Drawer";
import useEvent from "../../../../hooks/useEvent";
import { Button } from "@oc-labs/wt-storybook";
import useModal from "../../../../hooks/useModal";
import PropTypes from "prop-types";
import toast from "react-hot-toast";

export default function MembersDrawer({ event, attendees, reloadEvent, reloadAttendees }) {
  const { getMembers, sendInvites } = useEvent();
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const { isOpen, close } = useModal('members-drawer');

  useEffect(() => {
    if (!event?.id) return;

    const fetchMembers = async () => {
      try {
        const list = await getMembers();

        setMembers(
          list.map((user) => ({
            ...user,
            displayName: user.name || user.email,
            isAttending: attendees?.some((a) => a.id === user.id) || false,
          }))
        );
      } catch (e) {
        console.error("Failed to load members", e);
      }
    };

    fetchMembers();
  }, [event?.id, attendees]);

  // Manejo de selección de miembros
  const handleClickMember = (member) => {
    if (member.isAttending) return;

    setSelectedMembers((prev) =>
      prev.includes(member.id)
        ? prev.filter((id) => id !== member.id)
        : [...prev, member.id]
    );
  };

  // Enviar invitaciones
  const handleSendInvite = async () => {
    try {
      setIsInviting(true);
      await sendInvites(event.id, selectedMembers);
      setSelectedMembers([]);
      if (reloadEvent) reloadEvent();
      if (reloadAttendees) await reloadAttendees();
      toast.success("Invitations sent");
      close();
    } catch (e) {
      console.error("Error sending invites", e);
      toast.error("Failed to send invites");
    } finally {
      setIsInviting(false);
    }
  };

  // Ordenar y filtrar sin mutar estado
  const sortedMembers = [...members].sort((a, b) =>
    a.isAttending === b.isAttending ? 0 : a.isAttending ? 1 : -1
  );

  const filteredMembers = sortedMembers.filter(
    (m) =>
      m.displayName.toLowerCase().includes(searchFilter.toLowerCase()) ||
      (m.email || '').toLowerCase().includes(searchFilter.toLowerCase())
  );

  return (
    <Drawer
      name="members-drawer"
      title="Members"
      className="h-full overflow-y-auto flex flex-col !px-0 !pl-4"
    >
      {/* Buscador */}
      <div className="mb-4 pr-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search members"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full h-11 pl-10 pr-4 rounded-full bg-white outline-none text-sm placeholder-gray-400 border border-gray-300 focus:border-black transition"
          />
          <img
            src="/icons/search.svg"
            alt="Search icon"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
          />
        </div>
      </div>

      {/* Lista de miembros */}
      <ul className="flex-1 overflow-y-auto">
        {filteredMembers.map((m) => {
          const isSelected = selectedMembers.includes(m.id);
          return (
            <li
              key={m.id}
              className={`flex items-center justify-between py-3 pr-4 ${
                m.isAttending ? "opacity-35" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <img
                  src={`https://api.dicebear.com/9.x/initials/svg?seed=${m.displayName}`}
                  alt={m.displayName}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div>
                  <div className="text-sm max-w-[12rem] truncate">
                    {m.displayName}
                  </div>
                  <div className="text-xs text-gray-500 max-w-[12rem] truncate">
                    {m.email}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleClickMember(m)}
                aria-pressed={m.isAttending || isSelected}
                className={`relative w-6 h-6 rounded-full border border-gray-400 flex items-center justify-center ${
                  m.isAttending || isSelected
                    ? "bg-black border-black"
                    : "bg-transparent"
                }`}
              >
                {(m.isAttending || isSelected) && (
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-200" />
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Botón de invitación */}
      <Button
        className="mt-4"
        onClick={handleSendInvite}
        disabled={selectedMembers.length === 0 || isInviting}
      >
        Invite Selected Members
      </Button>
    </Drawer>
  );
}

MembersDrawer.propTypes = {
  event: PropTypes.object.isRequired,
  attendees: PropTypes.array.isRequired,
  reloadEvent: PropTypes.func,
  reloadAttendees: PropTypes.func,
};